import { TextBody } from '@/design-system/components/typography';
import { LayoutContext, ScreenSize } from 'contexts/LayoutContext';
import { ActivityIndicator, FlatList, H2, H6, SafeAreaView, ScrollView, SxProp, View } from 'dripsy';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GetBrokersResponseItem } from '@utx/utx-client';
import { useGetCalcFee } from '../../hooks/useGetCalcFee.hook';
import { useSelectBrokers } from '../../hooks/useSelectBrokers.hook';
import ExchangeImageBackground from '../background/exchange-image-background';
import { SelectExchangeItem } from './SelectExchangeItem.component';

export interface SelectExchangeWebProps {}

export const SelectExchangeWebScreen: React.FC<SelectExchangeWebProps> = () => {
  const selectBrokers = useSelectBrokers();
  const layout = useContext(LayoutContext);
  const calcFee = useGetCalcFee({ enabledFetchAppFee: false, enabledFetchMonthlyFee: false });
  const isFullDesktop = layout.screenSize === ScreenSize.DesktopFull;
  const { t } = useTranslation('exchange-sub-account');

  useEffect(() => {
    selectBrokers.getBrokers.refetch();
    calcFee.applicationData.refetch();
    calcFee.monthlyData.refetch();
  }, []);

  const onResult = () => {};

  return (
    <View sx={sxStyles.container}>
      <ExchangeImageBackground />
      <SafeAreaView sx={sxStyles.safeView}>
        <ScrollView>
          <View sx={sxStyles.body}>
            <View sx={sxStyles.columnView}>
              <View sx={sxStyles.titleView}>
                <H2 sx={sxStyles.title}>{t('exchange-account::account-selection.title--make-your-move')}</H2>
              </View>
              <View sx={sxStyles.introTextView}>
                <H6 sx={sxStyles.introText}>{t('exchange-account::account-selection.text--create-account-now')}</H6>
              </View>
              <View sx={sxStyles.gridView}>
                {selectBrokers.getBrokers.isLoading && <ActivityIndicator size={'large'} />}
                {!selectBrokers.getBrokers.isLoading && selectBrokers.getBrokers.brokers.length === 0 && (
                  <TextBody bold sx={{ color: '$danger' }}>
                    {t('exchange-account::zero-state.title--your-portfolio-is-empty')}
                  </TextBody>
                )}
                {isFullDesktop ? (
                  <FlatList
                    key={'FullDesktop'}
                    data={selectBrokers.getBrokers.brokers}
                    renderItem={({ item, index }) => {
                      return <SelectExchangeItem data={item as GetBrokersResponseItem} calcFee={calcFee} onResult={onResult} />;
                    }}
                    keyExtractor={(item: any) => item.id}
                    numColumns={3}
                  />
                ) : (
                  <FlatList
                    key={'FullTablet'}
                    data={selectBrokers.getBrokers.brokers}
                    renderItem={({ item, index }) => {
                      return <SelectExchangeItem data={item as GetBrokersResponseItem} calcFee={calcFee} onResult={onResult} />;
                    }}
                    keyExtractor={(item: any) => item.id}
                    numColumns={2}
                  />
                )}
              </View>
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    flex: 1,
    backgroundColor: '$backgroundPrimary',
    height: '100%'
  },
  item: {
    flex: 1,
    margin: '$5',
    borderRadius: '$radius2'
  },
  itemView: {
    height: '50%',
    padding: '$5'
  },
  itemContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center'
  },
  safeView: {
    flex: 1,
    mt: '$7',
    flexDirection: 'column'
  },
  body: {
    pt: '$4',
    pb: '$4',
    borderRadius: '$radius3',
    ml: '$4',
    mr: '$4',
    mt: '$4'
  },
  columnView: {
    flexDirection: 'column',
    alignItems: 'center'
  },
  titleView: {
    width: '90%'
  },
  title: {
    textAlign: 'center',
    color: '$textTitle'
  },
  introTextView: {
    mt: '$5'
  },
  introText: {
    color: '$textDarkBackground',
    textAlign: 'center',
    fontSize: 20
  },
  gridView: {
    mt: '$7',
    mb: '$3',
    mr: '$2',
    ml: '$2',
    flex: 1
  }
};
